import {
  ChangeEvent,
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
} from 'react'
import { RichTextEditorComponent } from '@syncfusion/ej2-react-richtexteditor'
import { getNoteReadOnlyUrl, getNoteUploadUrl } from '@services/api/note.api'
import { NoteDraft } from '../NoteTextEditor.types'
import { logger } from '@utils/safeLogger'

type UseNoteImageUploadArgs = {
  rteRef: RefObject<RichTextEditorComponent>
  noteImageInputRef: RefObject<HTMLInputElement>
  setData: Dispatch<SetStateAction<NoteDraft>>
  onUpdate?: (data: NoteDraft[]) => void
}

type ActionBeginArgs = {
  requestType?: string
  cancel?: boolean
}

type UseNoteImageUploadResult = {
  onActionBegin: (args: ActionBeginArgs) => void
  handleNoteImageSelect: (e: ChangeEvent<HTMLInputElement>) => Promise<void>
}

export const useNoteImageUpload = ({
  rteRef,
  noteImageInputRef,
  setData,
  onUpdate,
}: UseNoteImageUploadArgs): UseNoteImageUploadResult => {
  const handleInsertImageClick = useCallback((): void => {
    noteImageInputRef.current?.click()
  }, [noteImageInputRef])

  const onActionBegin = useCallback(
    (args: ActionBeginArgs): void => {
      if (args.requestType === 'Image') {
        args.cancel = true
        handleInsertImageClick()
      }
    },
    [handleInsertImageClick],
  )

  const handleNoteImageSelect = useCallback(
    async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file || !rteRef.current) return

      const contentType = file.type || 'image/png'
      const filename = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`

      try {
        const uploadUrlRes = await getNoteUploadUrl({ contentType, filename })
        if (uploadUrlRes.statusCode !== 200 || !uploadUrlRes.data?.upload_url) {
          logger.error('Failed to get upload URL', uploadUrlRes)
          return
        }

        const { upload_url, blob_name } = uploadUrlRes.data
        const putRes = await fetch(upload_url, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': contentType,
            'x-ms-blob-type': 'BlockBlob',
          },
        })
        if (!putRes.ok) {
          logger.error('Failed to upload image to Azure', putRes)
          return
        }

        const readOnlyRes = await getNoteReadOnlyUrl(blob_name)
        if (
          readOnlyRes.statusCode !== 200 ||
          !readOnlyRes.data?.read_only_url
        ) {
          logger.error('Failed to get read-only URL', readOnlyRes)
          return
        }

        const readOnlyUrl = readOnlyRes.data.read_only_url
        const escapedBlob = blob_name.replace(/"/g, '&quot;')
        const escapedUrl = readOnlyUrl.replace(/"/g, '&quot;')
        const imgHtml = `<img data-blob="${escapedBlob}" src="${escapedUrl}" alt="" />`
        rteRef.current.executeCommand('insertHTML', imgHtml)

        setTimeout(() => {
          const updatedContent = rteRef.current?.value ?? ''
          setData((prev) => {
            const next = { ...prev, content: updatedContent }
            onUpdate?.([next])
            return next
          })
        }, 100)
      } catch (err: unknown) {
        logger.error('Note image upload failed', err)
      }
    },
    [onUpdate, rteRef, setData],
  )

  return {
    onActionBegin,
    handleNoteImageSelect,
  }
}
